import cx from 'classnames';
import { Field } from 'formik';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';

import { getSupportedHardware, HardwareTypes } from '../../../api/Optimise';
import { OnboardingContext } from '../../../context/OnboardingContext';
import BasicSpinner from '../../../ui/BasicSpinner';
import ErrorLabel from '../../../ui/ErrorLabel';
import { StyledCard, StyledCardBody, StyledHeader, StyledIcon } from '../../../ui/Styled';
import { getUnitList } from '../../../utils/dataUtils';

const InverterDetails = () => {
  const theme = useTheme();
  const { inverterManufacturer, setInverterManufacturer, inverterUnitList, setInverterUnitList } =
    useContext(OnboardingContext);

  const [inverterConfigList, setInverterConfigList] = useState(null);
  const [hardwareError, setHardwareError] = useState(false);

  const selectInverterManufacturer = (manufacturer) => {
    setInverterManufacturer(manufacturer);
    setInverterUnitList(getUnitList(inverterConfigList?.manufacturer_list, manufacturer));
  };

  const fetchSupportedInverters = useCallback(() => {
    setHardwareError(false);
    getSupportedHardware(HardwareTypes.INVERTER)
      .then((supportedInverters) => setInverterConfigList(supportedInverters))
      .catch((e) => {
        console.error(e);
        setHardwareError(true);
      });
  }, []);

  useEffect(() => {
    fetchSupportedInverters();
  }, [fetchSupportedInverters]);

  useEffect(() => {
    if (inverterManufacturer) {
      selectInverterManufacturer(inverterManufacturer);
    }
  }, [inverterConfigList]);

  const StyledFormHeader = styled.h4`
    color: ${(props) => props.theme.colors.themedFormHeaders};
  `;

  return (
    <div className="mb-2 pb-2">
      <StyledCard>
        <StyledHeader>
          <StyledFormHeader>Inverter Details</StyledFormHeader>
          <StyledIcon icon="inverter">
            <img src={theme.icons.inverter} alt="Inverter" />
          </StyledIcon>
        </StyledHeader>
        <div>
          <StyledCardBody>
            {hardwareError && (
              <Form.Group>
                <ErrorLabel label="We couldn't load inverter manufacturers. Please try again." />
                <Button variant="link" className="p-0" onClick={fetchSupportedInverters}>
                  Try again
                </Button>
              </Form.Group>
            )}
            <Field name="inverterManufacturer">
              {({ field, meta, form }) => (
                <Form.Group controlId="inverterManufacturer">
                  <Form.Label>
                    Select inverter manufacturer
                    {!inverterConfigList && !hardwareError && <BasicSpinner />}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    size="md"
                    value={field.value}
                    onChange={(e) => {
                      selectInverterManufacturer(e.target.value);
                      field.onChange(e);
                      form.setFieldValue('inverterUnitName', '');
                    }}
                    onBlur={field.onBlur}
                    placeholder="Select inverter manufacturer"
                    className={cx({ error: !!meta.error })}
                    disabled={!inverterConfigList}
                  >
                    <optgroup>
                      <option value="" key="manufacturer-select">
                        Select inverter manufacturer
                      </option>
                      {inverterConfigList?.manufacturer_list?.map(({ manufacturer }, index) => (
                        <option key={`manufacturer-${index}`} value={manufacturer}>
                          {manufacturer}
                        </option>
                      ))}
                    </optgroup>
                  </Form.Control>
                  {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                </Form.Group>
              )}
            </Field>
            <Form.Group className="not-found-text">
              <div>
                Manufacturer not shown?
                <Button
                  variant="link"
                  href="https://share.hsforms.com/1gsc8DNuRSkObygSHhibu0A2uro2"
                  target="_blank"
                  className="p-0 not-found-text ml-1"
                >
                  Let us know
                </Button>
              </div>
            </Form.Group>
            <Field name="inverterUnitName">
              {({ field, meta }) => (
                <Form.Group controlId="inverterUnitName">
                  <Form.Label>Select inverter model</Form.Label>
                  <Form.Control
                    as="select"
                    size="md"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Select inverter model"
                    disabled={!inverterUnitList?.length}
                    className={cx({ error: !!meta.error })}
                  >
                    <optgroup>
                      <option value="" key="inverter-select">
                        Select inverter model
                      </option>
                      {inverterUnitList?.map(({ unit_name }, index) => (
                        <option key={`model-${index}`} value={unit_name}>
                          {unit_name}
                        </option>
                      ))}
                    </optgroup>
                  </Form.Control>
                  {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                </Form.Group>
              )}
            </Field>
          </StyledCardBody>
        </div>
      </StyledCard>
    </div>
  );
};

export default InverterDetails;
